import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "stepper" }
const _hoisted_2 = { class: "stepper-inner" }
const _hoisted_3 = ["step-count"]
const _hoisted_4 = { class: "step-icons" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "step-content" }
const _hoisted_7 = { class: "stat" }
const _hoisted_8 = ["show-arrow"]

import { ref, reactive, computed, onMounted } from "vue";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { ProductIds } from "@/types/types";
import { ApplicationStatusStep } from "@/types/stepper";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/24/solid";


export default /*@__PURE__*/_defineComponent({
  __name: 'DetailsStepper',
  setup(__props) {

const applicationStore = useApplicationStore();

const stats = ref<Array<ApplicationStatusStep>>([]);

const productTypeId = computed(() => applicationStore.viewModel.productTypeId);

const steps = computed(
  () => applicationStore.viewModel.applicationStatusStepper,
);

const paginationEnabled = computed<boolean>(() => {
  switch (productTypeId.value) {
    case ProductIds.PEL:
      return true;
    case ProductIds.LOC:
      return true;
    default:
      return false;
  }
});

const pagination = reactive({
  currentPage: 1,
  perPage: computed<number>((): number => {
    switch (productTypeId.value) {
      case ProductIds.PEL:
      case ProductIds.LOC:
        if (window.innerWidth < 375) {
          return 1;
        }
        return 3;
      case ProductIds.REFI:
        return steps.value.length;
      default:
        return steps.value.length;
    }
  }).value,
  totalItems: computed<number>((): number => stats.value.length),
  totalPages: computed<number>(() =>
    Math.ceil((steps.value || []).length / pagination.perPage),
  ),
  pages: computed<Array<number>>(() =>
    [...Array(pagination.totalPages).keys()].map((i) => i + 1),
  ),
});

const totalPages = computed<number>(() =>
  Math.ceil((stats.value || []).length / pagination.perPage),
);

const paginatedItems = computed(() => {
  const { currentPage, perPage, totalItems } = pagination;

  if (!currentPage) {
    return stats.value;
  } else {
    const start = (currentPage - 1) * perPage;
    let stop = start + perPage;
    let itemCount = Math.abs(stop - start);
    let numberOfItemsToPush = 0;

    if (stop > totalItems) {
      numberOfItemsToPush = Math.abs(stop - totalItems);
      stop -= numberOfItemsToPush;
      itemCount = Math.abs(stop - start);
    }

    const newItems = stats.value.slice(start, stop);

    return newItems;
  }
});

async function goNext() {
  if (pagination.currentPage >= pagination.totalPages) {
    pagination.currentPage = 1;
  } else {
    pagination.currentPage++;
  }
}

async function goBack() {
  if (pagination.currentPage === 1) {
    pagination.currentPage = pagination.pages[pagination.pages.length - 1];
  } else {
    pagination.currentPage--;
  }
}

function showArrow(index: number): boolean {
  if (
    productTypeId.value === ProductIds.PEL ||
    productTypeId.value === ProductIds.LOC
  ) {
    return false;
  } else {
    return index < stats.value.length - 1;
  }
}

onMounted(() => {
  stats.value = steps.value;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["stepper-container", {
      'left-arrow-hidden': paginationEnabled.value && pagination.currentPage <= 1,
      'right-arrow-hidden':
        paginationEnabled.value && pagination.currentPage >= totalPages.value,
    }])
  }, [
    (paginationEnabled.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "pagination left",
          role: "pagination",
          onClick: goBack
        }, [
          (paginationEnabled.value && pagination.currentPage > 1)
            ? (_openBlock(), _createBlock(_unref(ChevronLeftIcon), {
                key: 0,
                class: "pagination-icon"
              }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedItems.value, (item, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: item.name,
            class: _normalizeClass(['step', `step-${item.status}`]),
            "step-count": paginatedItems.value.length
          }, [
            _createElementVNode("div", _hoisted_4, [
              (item.status === 'complete')
                ? (_openBlock(), _createBlock(_unref(CheckCircleIcon), {
                    key: 0,
                    class: "step-icon"
                  }))
                : (item.status === 'active')
                  ? (_openBlock(), _createBlock(_unref(QuestionMarkCircleIcon), {
                      key: 1,
                      class: "step-icon"
                    }))
                  : (_openBlock(), _createBlock(_unref(ExclamationCircleIcon), {
                      key: 2,
                      class: "step-icon"
                    }))
            ]),
            _createElementVNode("span", {
              class: "step-title",
              innerHTML: item.name
            }, null, 8, _hoisted_5),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, _toDisplayString(item.date), 1)
            ]),
            (showArrow(index))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "step-arrow",
                  "show-arrow": showArrow(index)
                }, [
                  (_openBlock(), _createElementBlock("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    class: _normalizeClass(["arrow-icon", ['arrow-na', { 'arrow-na': item.date === 'N/A' }]]),
                    viewBox: "0 0 20 20",
                    fill: "currentColor"
                  }, [...(_cache[0] || (_cache[0] = [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M11 16H3V8h8V2l10 10l-10 10zm2-9v3H5v4h8v3l5-5z"
                    }, null, -1)
                  ]))], 2))
                ], 8, _hoisted_8))
              : _createCommentVNode("", true)
          ], 10, _hoisted_3))
        }), 128))
      ])
    ]),
    (paginationEnabled.value && pagination.currentPage < totalPages.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "pagination right",
          role: "pagination",
          onClick: goNext
        }, [
          _createVNode(_unref(ChevronRightIcon), { class: "pagination-icon" })
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})